import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { adminApi, StagingCanonicalUploadBody } from '../api/admin';
import { FROM_CLIENTVERSE_HINT, FROM_CLIENTVERSE_LABEL, fromClientverseChip } from '../modules/data-mapping/fromClientverse';
import ChangeTrackingReport from './ChangeTrackingReport';

// Admin-only browse + edit of a staging schema's descriptions and samples.
// Two modes, by route:
//   /admin/modules/data-mapping/staging/:version/browse — a canonical template
//   /admin/clients/:clientId/staging/browse              — a client's own schema
// The client edits are what that client sees in Data Mapping and its exports;
// template edits reach no client (each owns its copy). Two-pane layout: object
// list on the left, the selected object and its fields on the right. Edits
// save on blur — no explicit save button.
export default function AdminStagingCanonicalBrowser() {
  const { version, clientId } = useParams();
  // One key for the effect; the calls below branch on which kind it is.
  const target = clientId ? `client:${clientId}` : version ? `canonical:${version}` : null;
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [schema, setSchema] = useState<StagingCanonicalUploadBody | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  // Fields: browse and document. Change tracking: which tables can tell what
  // changed since the last push to ClientVerse.
  const [view, setView] = useState<'fields' | 'tracking'>('fields');

  useEffect(() => {
    if (!target) return;
    (async () => {
      try {
        const t = await getToken();
        const body = clientId
          ? await adminApi.exportClientStaging(t, clientId)
          : await adminApi.exportStagingCanonicalVersion(t, version!);
        setSchema(body);
        if (body.objects.length > 0) setSelected((prev) => prev ?? body.objects[0].name);
      } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const filteredObjects = useMemo(() => {
    if (!schema) return [];
    const q = query.trim().toLowerCase();
    if (!q) return schema.objects;
    return schema.objects.filter((o) =>
      o.name.toLowerCase().includes(q) || (o.label ?? '').toLowerCase().includes(q),
    );
  }, [schema, query]);

  const selectedObj = useMemo(
    () => schema?.objects.find((o) => o.name === selected) ?? null,
    [schema, selected],
  );

  // Merge an updated object back into the loaded schema so the UI reflects the save.
  const mutateSchema = (fn: (draft: StagingCanonicalUploadBody) => StagingCanonicalUploadBody) => {
    setSchema((cur) => (cur ? fn(cur) : cur));
  };

  const saveObjectDesc = async (objectName: string, description: string | null) => {
    if (!target) return;
    try {
      const t = await getToken();
      if (clientId) await adminApi.updateClientStagingObjectDescription(t, clientId, objectName, description);
      else await adminApi.updateStagingCanonicalObjectDescription(t, version!, objectName, description);
      mutateSchema((s) => ({
        ...s,
        objects: s.objects.map((o) => (o.name === objectName ? { ...o, description } : o)),
      }));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const saveFieldDesc = async (objectName: string, fieldName: string, description: string | null) => {
    if (!target) return;
    try {
      const t = await getToken();
      if (clientId) await adminApi.updateClientStagingFieldDescription(t, clientId, objectName, fieldName, description);
      else await adminApi.updateStagingCanonicalFieldDescription(t, version!, objectName, fieldName, description);
      mutateSchema((s) => ({
        ...s,
        objects: s.objects.map((o) => {
          if (o.name !== objectName) return o;
          return {
            ...o,
            fields: (o.fields ?? []).map((f) => (f.name === fieldName ? { ...f, description } : f)),
          };
        }),
      }));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const saveFieldSample = async (objectName: string, fieldName: string, sample_data: string | null) => {
    if (!target) return;
    try {
      const t = await getToken();
      if (clientId) await adminApi.updateClientStagingFieldSampleData(t, clientId, objectName, fieldName, sample_data);
      else await adminApi.updateStagingCanonicalFieldSampleData(t, version!, objectName, fieldName, sample_data);
      mutateSchema((s) => ({
        ...s,
        objects: s.objects.map((o) => {
          if (o.name !== objectName) return o;
          return {
            ...o,
            fields: (o.fields ?? []).map((f) => (f.name === fieldName ? { ...f, sample_data } : f)),
          };
        }),
      }));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const saveFieldFromClientverse = async (objectName: string, fieldName: string, from_clientverse: boolean) => {
    if (!target) return;
    try {
      const t = await getToken();
      if (clientId) await adminApi.updateClientStagingFieldFromClientverse(t, clientId, objectName, fieldName, from_clientverse);
      else await adminApi.updateStagingCanonicalFieldFromClientverse(t, version!, objectName, fieldName, from_clientverse);
      mutateSchema((s) => ({
        ...s,
        objects: s.objects.map((o) => {
          if (o.name !== objectName) return o;
          return {
            ...o,
            fields: (o.fields ?? []).map((f) => (f.name === fieldName ? { ...f, from_clientverse } : f)),
          };
        }),
      }));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const documented = useMemo(() => {
    if (!schema) return { objects: 0, fields: 0, totalFields: 0 };
    let objs = 0, docFields = 0, totalFields = 0;
    for (const o of schema.objects) {
      if ((o.description ?? '').trim()) objs++;
      for (const f of (o.fields ?? [])) {
        totalFields++;
        if ((f.description ?? '').trim()) docFields++;
      }
    }
    return { objects: objs, fields: docFields, totalFields };
  }, [schema]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        {clientId ? (
          <Link to={`/admin/clients/${clientId}`} style={breadcrumb}>Client</Link>
        ) : (
          <>
            <Link to="/admin/modules" style={breadcrumb}>Modules</Link>
            <span style={{ color: 'var(--mi-muted)' }}>›</span>
            <Link to="/admin/modules/data-mapping" style={breadcrumb}>Data Mapping</Link>
          </>
        )}
        <span style={{ color: 'var(--mi-muted)' }}>›</span>
        <h1 style={{ color: 'var(--mi-navy)', margin: 0 }}>
          {clientId ? `Staging schema${schema?.schema_version ? ` — v${schema.schema_version}` : ''}` : `Staging template — v${version}`}
        </h1>
        <button onClick={() => navigate(-1)} style={backBtn}>← Back</button>
      </div>
      <p style={{ color: 'var(--mi-muted)', marginTop: 4, fontSize: 13 }}>
        {clientId
          ? <>This client&rsquo;s own staging schema. Descriptions and samples save on blur, and are what the client sees in Data Mapping and its exports.</>
          : <>A canonical template. Descriptions and samples save on blur. Editing a template changes no client: each client owns its own copy, so apply the template to a client again to pass changes on.</>}
      </p>

      {error && <div style={errBox}>{error}</div>}
      {!schema && !error && <div style={{ color: 'var(--mi-muted)', fontSize: 13 }}>Loading…</div>}

      {schema && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--mi-muted)' }}>
              {schema.objects.length} objects · {documented.totalFields} fields ·
              {' '}<strong>{documented.objects}</strong> objects and{' '}
              <strong>{documented.fields}</strong> fields documented
            </div>
            <div role="tablist" style={{ display: 'flex', marginLeft: 'auto', border: '1px solid var(--mi-border)', borderRadius: 6, overflow: 'hidden' }}>
              {([['fields', 'Fields'], ['tracking', 'Change tracking']] as const).map(([v, label]) => (
                <button
                  key={v}
                  role="tab"
                  aria-selected={view === v}
                  onClick={() => setView(v)}
                  style={{
                    padding: '5px 12px', fontSize: 13, border: 'none', cursor: 'pointer',
                    background: view === v ? 'var(--mi-navy)' : '#fff', color: view === v ? '#fff' : 'var(--mi-navy)',
                  }}
                >{label}</button>
              ))}
            </div>
          </div>

          {view === 'tracking' ? (
            <ChangeTrackingReport
              schema={schema}
              fileStem={clientId ? 'client-staging' : `staging-template-v${version}`}
              onOpenTable={(name) => { setSelected(name); setView('fields'); }}
            />
          ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 12, alignItems: 'start' }}>
            {/* LEFT: object picker */}
            <div className="card" style={{ height: 720, display: 'flex', flexDirection: 'column', padding: 12 }}>
              <input
                placeholder="Filter objects…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={input}
              />
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginTop: 8 }}>
                {filteredObjects.length === 0 ? (
                  <div style={{ color: 'var(--mi-muted)', fontSize: 13, padding: 6 }}>No matches.</div>
                ) : (
                  filteredObjects.map((o) => {
                    const hasDesc = !!(o.description ?? '').trim();
                    const fieldTotal = o.fields?.length ?? 0;
                    const fieldDoc = (o.fields ?? []).filter((f) => (f.description ?? '').trim()).length;
                    const isSel = selected === o.name;
                    return (
                      <button
                        key={o.name}
                        onClick={() => setSelected(o.name)}
                        style={{
                          ...listItem,
                          background: isSel ? '#eef0f8' : '#fff',
                          borderColor: isSel ? 'var(--mi-purple)' : 'var(--mi-border)',
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'monospace', color: 'var(--mi-navy)' }}>
                          {o.name}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--mi-muted)', marginTop: 2 }}>
                          {hasDesc && <span title="Object has description" style={{ marginRight: 6, color: 'var(--mi-green)' }}>●</span>}
                          {fieldDoc}/{fieldTotal} fields documented
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* RIGHT: object detail + fields */}
            <div style={{ minWidth: 0 }}>
              {!selectedObj ? (
                <div className="card" style={{ color: 'var(--mi-muted)' }}>Select an object.</div>
              ) : (
                <ObjectEditor
                  key={selectedObj.name}
                  obj={selectedObj}
                  onSaveObjectDesc={(desc) => saveObjectDesc(selectedObj.name, desc)}
                  onSaveFieldDesc={(fieldName, desc) => saveFieldDesc(selectedObj.name, fieldName, desc)}
                  onSaveFieldSample={(fieldName, sample) => saveFieldSample(selectedObj.name, fieldName, sample)}
                  onSaveFieldFromClientverse={(fieldName, v) => saveFieldFromClientverse(selectedObj.name, fieldName, v)}
                />
              )}
            </div>
          </div>
          )}
        </>
      )}
    </div>
  );
}

function ObjectEditor({ obj, onSaveObjectDesc, onSaveFieldDesc, onSaveFieldSample, onSaveFieldFromClientverse }: {
  obj: StagingCanonicalUploadBody['objects'][number];
  onSaveObjectDesc: (description: string | null) => Promise<void>;
  onSaveFieldDesc: (fieldName: string, description: string | null) => Promise<void>;
  onSaveFieldSample: (fieldName: string, sample_data: string | null) => Promise<void>;
  onSaveFieldFromClientverse: (fieldName: string, from_clientverse: boolean) => Promise<void>;
}) {
  const [fieldQuery, setFieldQuery] = useState('');
  const filteredFields = useMemo(() => {
    const fields = obj.fields ?? [];
    const q = fieldQuery.trim().toLowerCase();
    if (!q) return fields;
    return fields.filter((f) =>
      f.name.toLowerCase().includes(q)
        || (f.label ?? '').toLowerCase().includes(q)
        || (f.description ?? '').toLowerCase().includes(q),
    );
  }, [obj.fields, fieldQuery]);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: 'var(--mi-navy)' }}>{obj.name}</div>
        <div style={{ fontSize: 12, color: 'var(--mi-muted)', marginTop: 2 }}>
          {obj.fields?.length ?? 0} fields
          {obj.source && <> · source <code>{obj.source}</code></>}
        </div>
      </div>

      <label style={fieldLabel}>Object description</label>
      <AutoSaveTextarea
        value={obj.description ?? ''}
        placeholder="Describe what this staging object represents…"
        onSave={(v) => onSaveObjectDesc(v.trim() ? v : null)}
        rows={3}
      />

      <div style={{ marginTop: 20, marginBottom: 8, display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: 'var(--mi-navy)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Fields
        </div>
        <input
          placeholder="Filter fields…"
          value={fieldQuery}
          onChange={(e) => setFieldQuery(e.target.value)}
          style={{ ...input, maxWidth: 240 }}
        />
        <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>
          {filteredFields.length} of {obj.fields?.length ?? 0} shown
        </span>
      </div>

      <div style={{ maxHeight: 560, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filteredFields.map((f) => (
          <div key={f.name} style={fieldRow}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: 'var(--mi-navy)' }}>{f.name}</span>
              {(f.sql_type || f.data_type) && (
                <span style={typeChip}>{f.sql_type || f.data_type}</span>
              )}
              {f.is_primary_key && <span style={pkChip}>PK</span>}
              {f.is_required && <span style={requiredChip}>required</span>}
              {f.from_clientverse && <span style={fromClientverseChip} title={FROM_CLIENTVERSE_HINT}>{FROM_CLIENTVERSE_LABEL}</span>}
              {f.length !== null && f.length !== undefined && f.length > 0 && (
                <span style={{ fontSize: 11, color: 'var(--mi-muted)' }}>len {f.length}</span>
              )}
              <label style={cvToggle} title="Clients see this field marked, and are told to leave it empty">
                <input
                  type="checkbox"
                  checked={!!f.from_clientverse}
                  onChange={(e) => { void onSaveFieldFromClientverse(f.name, e.target.checked); }}
                />
                From ClientVerse (client leaves it empty)
              </label>
            </div>
            <AutoSaveTextarea
              value={f.description ?? ''}
              placeholder="Describe this field…"
              onSave={(v) => onSaveFieldDesc(f.name, v.trim() ? v : null)}
              rows={2}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <span style={sampleLabel}>Sample</span>
              <AutoSaveTextarea
                value={f.sample_data ?? ''}
                placeholder="e.g. Acme Corporation / 2026-08-19 / 1250.00"
                onSave={(v) => onSaveFieldSample(f.name, v.trim() ? v : null)}
                singleLine
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Save-on-blur input. Renders a large single-line <input> when
// `singleLine` is set, otherwise a multi-line <textarea>. Fires `onSave`
// only when the value actually changed and shows a small status hint while
// a save is in flight and briefly after success.
function AutoSaveTextarea({ value, placeholder, onSave, rows, singleLine }: {
  value: string;
  placeholder?: string;
  onSave: (v: string) => Promise<void>;
  rows?: number;
  singleLine?: boolean;
}) {
  const [text, setText] = useState(value);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const original = useRef(value);
  useEffect(() => { setText(value); original.current = value; }, [value]);

  const handleBlur = async () => {
    if (text === original.current) return;
    setStatus('saving');
    try {
      await onSave(text);
      original.current = text;
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 1200);
    } catch {
      setStatus('error');
    }
  };

  return (
    // `flex: 1, minWidth: 0` lets this component fill remaining space when
    // it's a child of a flex row (Sample field beside its label). In a plain
    // block parent (the description above) it's a no-op.
    <div style={{ position: 'relative', marginTop: singleLine ? 0 : 4, flex: 1, minWidth: 0 }}>
      {singleLine ? (
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          style={largeInput}
        />
      ) : (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          rows={rows ?? 2}
          style={textarea}
        />
      )}
      {status !== 'idle' && (
        <span style={{
          position: 'absolute', right: 6, bottom: singleLine ? 8 : 4, fontSize: 11,
          color: status === 'error' ? '#a11414' : status === 'saved' ? 'var(--mi-green)' : 'var(--mi-muted)',
        }}>
          {status === 'saving' ? 'saving…' : status === 'saved' ? 'saved' : 'error'}
        </span>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
const breadcrumb: React.CSSProperties = { color: 'var(--mi-muted)', fontSize: 14, textDecoration: 'none' };
const backBtn: React.CSSProperties = { marginLeft: 'auto', background: 'transparent', border: '1px solid var(--mi-border)', padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: 'var(--mi-muted)' };
const errBox: React.CSSProperties = { background: '#fee', color: '#900', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 14 };
const input: React.CSSProperties = { width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--mi-border)', fontSize: 13, boxSizing: 'border-box' };
const listItem: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left', border: '1px solid var(--mi-border)',
  borderRadius: 6, padding: '8px 10px', cursor: 'pointer', marginBottom: 6, color: 'var(--mi-text)',
};
const fieldLabel: React.CSSProperties = { fontSize: 12, color: 'var(--mi-muted)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 };
const sampleLabel: React.CSSProperties = { fontSize: 10, color: 'var(--mi-muted)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700, minWidth: 44 };
const fieldRow: React.CSSProperties = { border: '1px solid var(--mi-border)', borderRadius: 6, padding: '8px 10px', background: '#fff' };
const textarea: React.CSSProperties = {
  width: '100%', padding: '6px 30px 6px 8px', borderRadius: 6, border: '1px solid var(--mi-border)',
  fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.4,
};
// Wider, taller single-line input for the Sample field. Monospace so example
// values (dates, ids, JSON snippets) stay visually aligned across rows.
const largeInput: React.CSSProperties = {
  width: '100%', padding: '10px 60px 10px 12px', borderRadius: 6,
  border: '1px solid var(--mi-border)',
  fontSize: 14, boxSizing: 'border-box', fontFamily: 'monospace',
  background: '#fbfdff',
};
const typeChip: React.CSSProperties = {
  display: 'inline-block', padding: '1px 6px', fontSize: 11, borderRadius: 4,
  background: '#eef2ff', color: '#3b4ea1', border: '1px solid #d6deff', fontFamily: 'monospace', whiteSpace: 'nowrap',
};
const pkChip: React.CSSProperties = {
  display: 'inline-block', padding: '1px 6px', fontSize: 11, borderRadius: 4,
  background: '#fff4cf', color: '#7a5b00', border: '1px solid #f1d77a', fontWeight: 700, fontFamily: 'monospace', letterSpacing: 0.5, whiteSpace: 'nowrap',
};
const cvToggle: React.CSSProperties = {
  marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4,
  fontSize: 12, color: 'var(--mi-muted)', cursor: 'pointer', whiteSpace: 'nowrap',
};
const requiredChip: React.CSSProperties = {
  display: 'inline-block', padding: '1px 6px', fontSize: 11, borderRadius: 4,
  background: '#fde7e7', color: '#a11414', border: '1px solid #f3c0c0', fontWeight: 600, whiteSpace: 'nowrap',
};
